import { jwtDecode } from 'jwt-decode';
import * as localStore from '../../../store/safeLocalStorage';

/**
 * An admin session ("Log in as this user"): the session token has the claim
 * `loginAsAdmin: "<admin email>"`. The API does not refresh such a token, and it lives one hour.
 *
 * Pure reads only, with no store import, so the code that runs before the app boots can use them.
 */

/** The admin email of a decoded session token, or null for a normal session. */
export function loginAsAdminOf(decoded: unknown): string | null {
  if (decoded === null || typeof decoded !== 'object') {
    return null;
  }
  const { loginAsAdmin } = decoded as { loginAsAdmin?: unknown };
  return typeof loginAsAdmin === 'string' && loginAsAdmin !== '' ? loginAsAdmin : null;
}

function decodeToken(token: string | null | undefined): { exp?: unknown } | null {
  if (token === null || token === undefined || token === '') {
    return null;
  }
  try {
    return jwtDecode<{ exp?: unknown }>(token);
  } catch {
    return null;
  }
}

/** The admin email of a session token, or null for a normal session or a token that does not decode. */
export function loginAsAdminOfToken(token: string | null | undefined): string | null {
  return loginAsAdminOf(decodeToken(token));
}

/** Seconds until the token expires. 0 or less means it is expired. Null when the token has no `exp`. */
export function secondsUntilExpiry(token: string, nowMs: number = Date.now()): number | null {
  const exp = decodeToken(token)?.exp;
  if (typeof exp !== 'number') {
    return null;
  }
  return exp - Math.floor(nowMs / 1000);
}

/** Whether the session that this browser stored is an admin session. Read before the app boots. */
export function storedSessionIsLoginAs(): boolean {
  const token = localStore.get<unknown>('userToken');
  return typeof token === 'string' && loginAsAdminOfToken(token) !== null;
}
